import {
  WIDGET_CLASS_CONSTRAINTS,
  BREAKPOINT_COLS,
} from '../../../../components/WidgetGrid/constants';
import {
  buildStackedLayout,
  expandWidgetsToAvailableSpace,
} from '../../../../components/WidgetGrid/utils';

const ACTION_MAP = {
  refresh: { key: 'BTN_REFRESH', icon: 'autorenew', tooltip: 'Update', action: { type: 'API' } },
  expand: { key: 'BTN_EXPAND', icon: 'arrowOutward', tooltip: 'View all', action: { type: 'NAVIGATE' } },
  add: { key: 'BTN_ADD', icon: 'plus', tooltip: 'Add', action: { type: 'SLIDEOUT' } },
};

const DEFAULT_CONSTRAINTS = WIDGET_CLASS_CONSTRAINTS.CLASS_2_STANDARD;

const toHeaderButtons = (actions) => {
  if (!Array.isArray(actions)) return [];
  return actions.map((a) => ACTION_MAP[a]).filter(Boolean);
};

const toConstraints = (widgetClass) =>
  WIDGET_CLASS_CONSTRAINTS[widgetClass] || DEFAULT_CONSTRAINTS;

/**
 * Converts a single editor widget into the shape WidgetGrid / WidgetWrapper expects.
 * Adds a `_builderType` field so renderWidget can resolve preview data by type.
 *
 * Handles two sources:
 *  - Library-added widgets: use `type` and `defaultActions`
 *  - API/mock-loaded widgets: use `widgetType` and `actions`
 */
const adaptWidget = (widget) => {
  const widgetType = widget.type || widget.widgetType;
  const actions = widget.defaultActions || widget.actions;
  const constraints = toConstraints(widget.widgetClass);

  return {
    instance_uuid: widget.instanceId,
    widget_definition_uuid: `builder-${widgetType || 'widget'}-def`,
    widget_type: 'CUSTOM_FIXED',
    accepts_global_filters: false,
    layout: widget.layout || { x: 0, y: 0, w: 4, h: 5 },
    constraints,
    config: {
      card: {
        title: widget.name || '',
        description: widget.description || '',
        header_buttons: toHeaderButtons(actions),
      },
    },
    _builderType: widgetType,
    _locked: widget.locked ?? false,
  };
};

const buildLayoutItem = (adaptedWidget) => {
  const { layout, constraints, instance_uuid } = adaptedWidget;
  return {
    i: instance_uuid,
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
    ...constraints,
  };
};

const buildResponsiveLayouts = (lgLayout) => {
  if (!lgLayout?.length) return {};
  const expandedLg = expandWidgetsToAvailableSpace(lgLayout, BREAKPOINT_COLS.lg);
  const expandedMd = expandWidgetsToAvailableSpace(lgLayout, BREAKPOINT_COLS.md);
  const expandedSm = expandWidgetsToAvailableSpace(lgLayout, BREAKPOINT_COLS.sm);
  return {
    lg: expandedLg,
    md: expandedMd,
    sm: expandedSm,
    xs: buildStackedLayout(lgLayout, BREAKPOINT_COLS.xs),
    xxs: buildStackedLayout(lgLayout, BREAKPOINT_COLS.xxs),
  };
};

/**
 * Converts an array of editor widgets into the `externalWidgetConfig` shape
 * that WidgetGrid expects: { widgets, layouts, gridConfig }.
 */
export const adaptWidgetsForGrid = (editorWidgets) => {
  if (!editorWidgets?.length) return { widgets: [], layouts: {}, gridConfig: null };

  const widgets = editorWidgets.map(adaptWidget);
  const lgLayout = widgets.map(buildLayoutItem);
  const layouts = buildResponsiveLayouts(lgLayout);

  return {
    widgets,
    layouts,
    gridConfig: { cols: 12, rowHeight: 24 },
  };
};
